// notifications.js - Enhanced with ZERO breaking changes
import { toast } from "react-toastify";
import i18n from "i18next";

/**
 * Internal helper to translate dynamic error messages
 */
const translateDynamicMessage = (message) => {
  if (!message) return message;

  // Pattern 1: "EntityType with the field_name field_name: value already exists"
  // Example: "Bank with the name name: naya bank already exists"
  const pattern1 =
    /^(\w+)\s+with\s+(?:the\s+)?(\w+)\s+(\w+):\s+(.+?)\s+already exists$/i;
  const match1 = message.match(pattern1);

  if (match1) {
    const [, entityType, , fieldName, value] = match1;

    const entityKey = entityType.toLowerCase().replace(/\s+/g, "_");
    const fieldKey = fieldName.toLowerCase().replace(/\s+/g, "_");

    return i18n.t("errors.duplicate_with_field", {
      entity: i18n.t(`entities.${entityKey}`, { defaultValue: entityType }),
      field: i18n.t(`fields.${fieldKey}`, { defaultValue: fieldName }),
      value: value,
      defaultValue: message,
    });
  }

  // Pattern 2: "value already exists"
  // Example: "naya bank already exists"
  const pattern2 = /^(.+?)\s+already exists$/i;
  const match2 = message.match(pattern2);

  if (match2) {
    const [, value] = match2;

    return i18n.t("errors.duplicate_value", {
      value: value,
      defaultValue: message,
    });
  }

  // Pattern 3: "EntityType with field_name: value already exists"
  // Example: "ServiceGroup with group_name: Marketing already exists"
  const pattern3 = /^(\w+)\s+with\s+(\w+):\s+(.+?)\s+already exists$/i;
  const match3 = message.match(pattern3);

  if (match3) {
    const [, entityType, fieldName, value] = match3;

    const entityKey = entityType.toLowerCase().replace(/\s+/g, "_");
    const fieldKey = fieldName.toLowerCase().replace(/\s+/g, "_");

    return i18n.t("errors.duplicate_entry", {
      entity: i18n.t(`entities.${entityKey}`, { defaultValue: entityType }),
      field: i18n.t(`fields.${fieldKey}`, { defaultValue: fieldName }),
      value: value,
      defaultValue: message,
    });
  }

  // If no pattern matches, return original message
  return message;
};

/**
 * Smart translation wrapper - checks if dynamic translation is needed
 */
const smartTranslate = (message) => {
  if (!message) return "";

  // Check if message contains patterns that need dynamic translation
  const isDynamicError = /already exists|with\s+\w+:/i.test(message);

  if (isDynamicError) {
    // Use dynamic translation
    return translateDynamicMessage(message);
  }

  // Use standard i18n translation
  return i18n.t(message, { defaultValue: message });
};

// ============================================
// ORIGINAL FUNCTIONS - ENHANCED BUT COMPATIBLE
// ============================================

/**
 * Show success notification
 * ENHANCED: Now handles both static keys and dynamic messages
 * BACKWARD COMPATIBLE: Works exactly as before for static keys
 */
export const notifySuccess = (message) => {
  const translatedMessage = smartTranslate(message);
  toast.success(translatedMessage);
};

/**
 * Show warning notification
 * ENHANCED: Now handles both static keys and dynamic messages
 * BACKWARD COMPATIBLE: Works exactly as before for static keys
 */
export const notifyWarn = (message) => {
  const translatedMessage = smartTranslate(message);
  toast.warn(translatedMessage);
};

/**
 * Show error notification (if you use it)
 * ENHANCED: Now handles both static keys and dynamic messages
 */
export const notifyError = (message) => {
  const translatedMessage = smartTranslate(message);
  toast.error(translatedMessage);
};

/**
 * Show info notification (if you use it)
 * ENHANCED: Now handles both static keys and dynamic messages
 */
export const notifyInfo = (message) => {
  const translatedMessage = smartTranslate(message);
  toast.info(translatedMessage);
};

// Export the helper for direct use in components (like AddNewModal)
export const translateDynamicError = translateDynamicMessage;
